import { useCallback, useContext, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { FieldValues, useForm } from 'react-hook-form';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { Ability } from '@casl/ability';
import { subDays } from 'date-fns';
import {
  Actions,
  CertificationAndLicense,
  ResourceHistoryChangeCategory,
  ResourceHistoryEntry,
  ResultStatus,
  Subjects,
} from '@dispatch/shared/types';
import { useGetResourceHistory } from '@dispatch/shared/hooks';
import { DateRangeField, TableComponent } from '@dispatch/web/ui';
import {
  buildEntryMemberNameResolver,
  buildSummaryDetail,
  formatTimestamp,
  renderActor,
} from './historyFormat';
import { HistoryDetailModal } from './HistoryDetailModal';
import {
  buildCertificationCatalogIndex,
  buildMemberCollections,
  buildMemberNameIndex,
} from './membershipValues';
import { ResourceContext } from '../ResourcesDetails';
import { useResourceHistoryFilters } from './useResourceHistoryFilters';

export type ResourceHistoryTabProps = {
  resourceId: string;
  permissions: Ability<[Actions, Subjects]> | undefined;
  /**
   * The company certification catalog. A certification's name is not in the
   * resource payload — only a key into this catalog — so it is the sole source for
   * naming certification history rows. Already fetched for the certs tab and
   * passed straight down; this adds no request.
   */
  certsAndLicense?: CertificationAndLicense[];
};

const CATEGORY_TO_TYPE_PARAM: Record<ResourceHistoryChangeCategory, string> = {
  Resource: 'resource',
  Schedule: 'schedule',
  Event: 'event',
};

const ALL_CATEGORIES: ResourceHistoryChangeCategory[] = [
  'Resource',
  'Schedule',
  'Event',
];

export function ResourceHistoryTab({
  resourceId,
  permissions,
  certsAndLicense,
}: ResourceHistoryTabProps) {
  const hasPermission = Boolean(permissions?.can?.('view', 'resource-audit'));

  const { control, watch } = useForm<FieldValues>({
    defaultValues: {
      dateRange: [subDays(new Date(), 30), new Date()],
    },
  });

  const {
    selectedCategories,
    toggleCategory,
    showUiChanges,
    setShowUiChanges,
    showApiChanges,
    setShowApiChanges,
  } = useResourceHistoryFilters();

  const [selectedEntry, setSelectedEntry] =
    useState<ResourceHistoryEntry | null>(null);

  // Membership audit rows carry only the member's GUID; resolve it to a display
  // name so both the grid summary and the modal show *which* member changed.
  // Almost everything resolves off the resource already in context:
  // offices/teams/groups/roles from its embedded company (stable entity GUIDs,
  // resolvable for created and deleted alike), and attribute definitions/groups,
  // locations, and nested attribute values from the resource's own current
  // collections (their audit GUID is the per-resource row id) — so for those, only
  // still-present rows resolve; a deleted one falls back to the bare verb.
  //
  // Certifications are the exception: their name is not in the resource payload at
  // all, so it comes from the company catalog already passed down for the certs
  // tab. No new fetch.
  const { data: resource } = useContext(ResourceContext);

  // Built once and shared: `buildMemberCollections` needs it to name an assigned
  // certification, and the per-entry resolver below needs the same index to name a
  // REMOVED one off the catalog key the audit itself recorded.
  const certificationNameByCatalogGuid = useMemo(
    () => buildCertificationCatalogIndex(certsAndLicense),
    [certsAndLicense]
  );

  const resolveMemberName = useMemo(
    () =>
      buildMemberNameIndex(
        buildMemberCollections(resource, certificationNameByCatalogGuid)
      ),
    [resource, certificationNameByCatalogGuid]
  );

  const resolveNameForEntry = useCallback(
    (changes: ResourceHistoryEntry['fieldChanges']) =>
      buildEntryMemberNameResolver(
        changes,
        resolveMemberName,
        certificationNameByCatalogGuid
      ),
    [resolveMemberName, certificationNameByCatalogGuid]
  );

  // Held here rather than called in the modal's JSX. Building the resolver walks the
  // entry's field changes and allocates two Maps, so calling it inline re-ran all of
  // that on every render of this tab — including every keystroke in the date filter —
  // and handed the modal a fresh prop identity each time.
  const resolveNameForSelectedEntry = useMemo(
    () => resolveNameForEntry(selectedEntry?.fieldChanges),
    [resolveNameForEntry, selectedEntry]
  );

  const dateRange = watch('dateRange') as [Date | null, Date | null];
  const from = dateRange?.[0] instanceof Date ? dateRange[0].toISOString() : '';
  const to = dateRange?.[1] instanceof Date ? dateRange[1].toISOString() : '';

  const typesParam = useMemo(
    () =>
      selectedCategories
        .map((category) => CATEGORY_TO_TYPE_PARAM[category])
        .join(','),
    [selectedCategories]
  );

  // Deselecting every category must show nothing — not fall back to "all"
  // categories server-side (an empty `types` param is dropped by the service).
  // So gate the fetch and render the empty state when none are selected.
  const hasCategoryFilter = selectedCategories.length > 0;

  const { data, status } = useGetResourceHistory({
    resourceId,
    from,
    to,
    types: typesParam,
    hasPermission,
    enabled: hasCategoryFilter,
  });

  // Row id -> full entry, so a row click can open the modal with complete
  // detail (the flattened grid row only carries the summary string).
  const entriesById = useMemo(() => {
    const map = new Map<string, ResourceHistoryEntry>();
    (data?.items ?? []).forEach((entry) => map.set(entry.id, entry));
    return map;
  }, [data?.items]);

  // Newest-first, filtered by the two source toggles. "UI" = user-initiated
  // rows, "API" = system (no-user) rows. Bulk actors are ALWAYS shown.
  const rows = useMemo(() => {
    if (!hasCategoryFilter) {
      return [];
    }
    return (data?.items ?? [])
      .filter((entry) => {
        if (entry.actor.type === 'user') {
          return showUiChanges;
        }
        if (entry.actor.type === 'system') {
          return showApiChanges;
        }
        return true;
      })
      .slice()
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
      .map((entry) => ({
        id: entry.id,
        timestamp: formatTimestamp(entry.timestamp),
        actor: renderActor(entry.actor),
        changeType: entry.changeCategory,
        detail: buildSummaryDetail(
          entry,
          resolveNameForEntry(entry.fieldChanges)
        ),
      }));
  }, [
    data?.items,
    showUiChanges,
    showApiChanges,
    hasCategoryFilter,
    resolveNameForEntry,
  ]);

  // With the fetch disabled while no category is selected, surface the empty
  // state (not a perpetual spinner) for that case.
  const listStatus: ResultStatus = hasCategoryFilter ? status : 'success';

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'timestamp', headerName: 'Date / Time', flex: 1.4 },
      { field: 'actor', headerName: 'Actor', flex: 1 },
      { field: 'changeType', headerName: 'Change Type', flex: 0.8 },
      {
        field: 'detail',
        headerName: 'Detail',
        flex: 2.2,
        // Truncated cells are unreadable for "large" changes, so surface the
        // full text on hover; a row click opens the modal for the complete view.
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={(params.value as string) ?? ''}>
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {params.value as string}
            </Box>
          </Tooltip>
        ),
      },
    ],
    []
  );

  const handleRowClick = (params: GridRowParams) => {
    const entry = entriesById.get(String(params.id));
    if (entry) {
      setSelectedEntry(entry);
    }
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <Box sx={{ '&&': { py: 3 } }} data-testid="resource-history-tab">
      <Stack spacing={2}>
        {/*
         * Controls are inset 24px while the banner + table stay full-bleed.
         * The parent ResourcesDetails drawer applies a blanket
         * `'& .MuiBox-root': { padding: 0 }` reset (specificity 0,2,0) that
         * beats a plain `sx={{ px: 3 }}` emotion class (0,1,0), silently
         * zeroing the inset. The `&&` self-reference raises specificity so the
         * inset wins deterministically without touching the shared drawer reset.
         */}
        <Box sx={{ '&&': { pl: 3, pr: 3 } }}>
          <Stack spacing={2}>
            <Box sx={{ maxWidth: 480 }}>
              <DateRangeField
                id="dateRange"
                name="dateRange"
                label="Date Range"
                control={control}
                startLabel="From"
                endLabel="To"
                separator="–"
                inputSx={{ '& .MuiInputBase-root': { mt: 4 } }}
              />
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {ALL_CATEGORIES.map((category) => {
                const selected = selectedCategories.includes(category);
                return (
                  <Chip
                    key={category}
                    label={category}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => toggleCategory(category)}
                    data-testid={`history-type-chip-${category}`}
                  />
                );
              })}
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showUiChanges}
                    onChange={(event) => setShowUiChanges(event.target.checked)}
                    data-testid="history-show-ui-changes"
                  />
                }
                label="Show changes done from UI"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showApiChanges}
                    onChange={(event) =>
                      setShowApiChanges(event.target.checked)
                    }
                    data-testid="history-show-api-changes"
                  />
                }
                label="Show changes done from API"
              />
            </Stack>
          </Stack>
        </Box>

        {data?.clampedTo90Days && (
          <Alert severity="info" data-testid="history-clamp-banner">
            Showing the most recent 90 days. Older changes were excluded from
            this view.
          </Alert>
        )}

        <HistoryList
          columns={columns}
          rows={rows}
          status={listStatus}
          onRowClick={handleRowClick}
        />

        <Box sx={{ '&&': { pl: 3, pr: 3 } }}>
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="history-footnote"
          >
            History reflects the resource&apos;s current schedules &amp; events.
          </Typography>
        </Box>
      </Stack>

      <HistoryDetailModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
        resolveName={resolveNameForSelectedEntry}
      />
    </Box>
  );
}

function HistoryList({
  columns,
  rows,
  status,
  onRowClick,
}: {
  columns: GridColDef[];
  rows: Array<Record<string, unknown>>;
  status: ResultStatus;
  onRowClick: (params: GridRowParams) => void;
}) {
  if (status === 'error') {
    return (
      <Alert severity="error" data-testid="history-error">
        Something went wrong loading history. Please try again.
      </Alert>
    );
  }

  // Keep the grid mounted for the loading and empty states so the spinner and
  // no-rows message render inside the DataGrid (matching the task-list pattern),
  // rather than swapping the whole list out for a standalone indicator.
  return (
    <TableComponent
      rows={rows}
      columns={columns}
      status={status}
      emptyEntityName="history record"
      tableHeight="55vh"
      clientPagination
      pagination={{ pageSize: 15, pagingOptions: [15, 25, 50] }}
      dataGridProps={{ onRowClick }}
    />
  );
}
